function resolvePolymarketProxyHost(envKey, devProxyPath, directHost) {
  const raw = String(process.env[envKey] ?? '').trim();
  if (raw) return raw.replace(/\/$/, '');
  if (process.env.NODE_ENV === 'development') return devProxyPath;
  return directHost;
}

export const GAMMA_HOST = resolvePolymarketProxyHost(
  'VUE_APP_POLYMARKET_GAMMA_URL',
  '/gamma-api',
  'https://gamma-api.polymarket.com'
);

/** 默认直连 CLOB；CORS 受限时可设 VUE_APP_POLYMARKET_CLOB_URL=/clob-api */
export const CLOB_HOST = resolvePolymarketProxyHost(
  'VUE_APP_POLYMARKET_CLOB_URL',
  'https://clob.polymarket.com',
  'https://clob.polymarket.com'
);

/** PM CLOB 盘口 WebSocket（与 PM 前端一致） */
export const POLYMARKET_CLOB_MARKET_WS_URL = (() => {
  const raw = String(process.env.VUE_APP_POLYMARKET_CLOB_WS_URL ?? '').trim();
  if (raw) return raw;
  return 'wss://ws-subscriptions-frontend-clob.polymarket.com/ws/market';
})();

/** 订阅 type：PM 前端为 markets；官方文档为 market */
export const POLYMARKET_CLOB_MARKET_WS_TYPE =
  String(process.env.VUE_APP_POLYMARKET_CLOB_WS_TYPE ?? 'markets').trim() || 'markets';

export const CLOB_MARKET_WS_PING_MS = 10000;
export const CLOB_MARKET_WS_RECONNECT_MS = 3000;

/** 官方 Polymarket RTDS（activity/trades 等；DeepSage 网关不代理） */
export const POLYMARKET_OFFICIAL_RTDS_URL = 'wss://ws-live-data.polymarket.com';

/**
 * 拒绝 ws://host:443（会被 302 到 HTTPS）。合法：wss://…/ws/polymarket 或本机 ws://127.0.0.1:19500/…
 * @param {string} raw
 */
export function normalizePolymarketRtdsWsUrl(raw) {
  const url = String(raw ?? '').trim();
  if (!url) return '';
  try {
    const parsed = new URL(url);
    if (parsed.protocol === 'ws:' && parsed.port === '443') {
      console.warn('[polymarket-rtds] refuse ws://*:443; use wss://host/ws/polymarket');
      return '';
    }
  } catch {
    console.warn('[polymarket-rtds] invalid WS URL', url);
    return '';
  }
  return url;
}

/**
 * DeepSage Chainlink 现货 WebSocket（替换直连 ws-live-data）。
 * env: VUE_APP_POLYMARKET_RTDS_WS_URL
 * - 绝对地址：wss://deepsage.app/ws/polymarket
 * - 相对路径：/ws/polymarket（开发走 vue.devServer 反代，需运行时拼 host）
 * - 留空：生产默认 wss://deepsage.app/ws/polymarket
 */
export function resolvePolymarketRtdsWsUrl() {
  const raw = String(process.env.VUE_APP_POLYMARKET_RTDS_WS_URL ?? '').trim();
  if (raw.startsWith('/')) {
    if (typeof window !== 'undefined' && window.location?.host) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}${raw}`;
    }
    return `ws://127.0.0.1:8080${raw}`;
  }
  const fromEnv = normalizePolymarketRtdsWsUrl(raw);
  if (fromEnv) return fromEnv;
  return 'wss://deepsage.app/ws/polymarket';
}

/** @deprecated 请用 resolvePolymarketRtdsWsUrl()（相对路径必须运行时解析） */
export const POLYMARKET_RTDS_URL = resolvePolymarketRtdsWsUrl();

/** 分钟盘 crypto-price / price-history 直连根地址（与 gamma-api 同类；PM 站点 API 默认无 CORS） */
export const POLYMARKET_CRYPTO_API_ORIGIN = (() => {
  const raw = String(process.env.VUE_APP_POLYMARKET_CRYPTO_API_URL ?? '').trim();
  if (raw) return raw.replace(/\/$/, '');
  return 'https://polymarket.com';
})();

/**
 * 浏览器 fetch 同源反代路径（polymarket.com/api 无 CORS，第三方站点需反代）。
 * 分钟盘 API 模式见 VUE_APP_POLYMARKET_CRYPTO_API_VIA（direct | pm | site）
 */
export const POLYMARKET_SITE_PROXY_PATH = (() => {
  const raw = String(process.env.VUE_APP_POLYMARKET_SITE_URL ?? '').trim();
  if (raw.startsWith('/')) return raw.replace(/\/$/, '');
  return '/polymarket-site';
})();

/** PM API 同源反代（Nginx: location /PM/ → https://polymarket.com/api） */
export const POLYMARKET_API_PROXY_PATH = (() => {
  const raw = String(process.env.VUE_APP_POLYMARKET_API_URL ?? '').trim();
  if (raw.startsWith('/')) return raw.replace(/\/$/, '');
  return '/PM';
})();

/** @deprecated 请用 POLYMARKET_SITE_PROXY_PATH */
export const POLYMARKET_SITE_HOST = POLYMARKET_SITE_PROXY_PATH;

/**
 * 分钟盘 crypto-price / price-history 请求方式。
 * - direct：浏览器直连 https://polymarket.com/api（多数环境会被 CORS 拦截，失败走 site 兜底）
 * - pm：/PM/crypto/...（dev + 已配 Nginx /PM/ 的生产）
 * - site：/polymarket-site/api/crypto/...（deepsage.app 等同源反代）
 */
function resolveCryptoApiViaMode() {
  return String(process.env.VUE_APP_POLYMARKET_CRYPTO_API_VIA ?? 'pm')
    .trim()
    .toLowerCase();
}

/** price-history / crypto-price 主路径 */
export function resolveCryptoPriceHistoryTargets() {
  const via = resolveCryptoApiViaMode();

  if (via === 'direct') {
    return [{ host: POLYMARKET_CRYPTO_API_ORIGIN, pathPrefix: '/api' }];
  }

  if (via === 'site') {
    const site = POLYMARKET_SITE_PROXY_PATH;
    return site ? [{ host: site, pathPrefix: '/api' }] : [];
  }

  const pm = POLYMARKET_API_PROXY_PATH;
  return pm ? [{ host: pm, pathPrefix: '' }] : [];
}

/** 主路径失败时的兜底（生产 Nginx 常只配 /PM/ 或 /polymarket-site/ 其一，双路径互备） */
export function resolveCryptoPriceHistoryFallbackTargets() {
  const via = resolveCryptoApiViaMode();
  const pm = POLYMARKET_API_PROXY_PATH;
  const site = POLYMARKET_SITE_PROXY_PATH;
  /** @type {{ host: string, pathPrefix: string }[]} */
  const fallbacks = [];

  if (via === 'direct') {
    if (site) fallbacks.push({ host: site, pathPrefix: '/api' });
    return fallbacks;
  }

  if (via === 'site') {
    if (pm && pm !== site) fallbacks.push({ host: pm, pathPrefix: '' });
    return fallbacks;
  }

  if (site && site !== pm) fallbacks.push({ host: site, pathPrefix: '/api' });
  return fallbacks;
}

/** @deprecated 使用 resolveCryptoPriceHistoryTargets */
export function resolveCryptoPriceHistoryHosts() {
  return resolveCryptoPriceHistoryTargets().map((t) => t.host);
}

export const REFRESH_MS = 15000;

/** WSS 连通时图表 REST 全量刷新降频（实时尾部由 live tick 追加） */
export const CHART_LIVE_STREAM_REFRESH_MS = 120000;

export const CHART_RANGE_KEYS = ['1H', '6H', '12H', '1D', '1W', '1M', 'ALL'];

/** interval / fidelity（分钟/点）/ candleSec（K 线桶宽，秒） */
export const RANGES = {
  '1H': { interval: '1h', fidelity: 1, candleSec: 120 },
  '6H': { interval: '6h', fidelity: 1, candleSec: 600 },
  '12H': { interval: '1d', fidelity: 5, candleSec: 900 },
  '1D': { interval: '1d', fidelity: 5, candleSec: 1800 },
  '1W': { interval: '1w', fidelity: 15, candleSec: 14400 },
  '1M': { interval: '1m', fidelity: 60, candleSec: 86400 },
  // max 区间点数有上限；fidelity=60 时往往只剩 ~2 月，改为按日采样以覆盖全生命周期
  ALL: { interval: 'max', fidelity: 1440, candleSec: 86400 },
};

export const SERIES_PALETTE = ['#4f8ff7', '#a855f7', '#f5921b', '#ec4899', '#22d3ee', '#16c784'];

export const CANDLE_UP = '#16c784';
export const CANDLE_DOWN = '#ea3943';
